import {CommonModule} from '@angular/common';
import {Component, inject, Input, OnInit, signal} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {filter, firstValueFrom, take} from 'rxjs';
import {GtNgDatepickerComponent} from '../../shared/commons/gt-ng-datepicker/gt-ng-datepicker.component';
import {DepartmentAutocompleteComponent} from '../../shared/commons/department-autocomplete/department-autocomplete.component';
import {
  StandardCodeAutocompleteComponent,
  StandardCodeOptionDto
} from '../../shared/commons/standard-code-autocomplete/standard-code-autocomplete.component';
import {FileUploadComponent} from '../user-business/add/file-upload/file-upload.component';
import {CertificateCreateRequest} from '../../../models/generated-models';
import {UserCertificateApiService} from './user-certificate-api.service';

export interface ThaidAuthData {
  pid: string;
  givenName: string;
  middleName: string;
  familyName: string;
  displayName: string;
}

export interface CertificateEditValue extends CertificateCreateRequest {
  id: number;
  attachmentUrl?: string;
  attachmentName?: string;
}

/** What the modal closes with: the request plus the scan to upload (or the wish to drop it). */
export interface CertificateModalResult extends CertificateCreateRequest {
  /** A newly picked file to upload after the save; undefined leaves the attachment as it is. */
  attachment?: File;
  /** The existing attachment should be removed after the save. */
  removeAttachment?: boolean;
}

function toLocalDateString(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}

const endDateNotBeforeTodayValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = control.value;
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  return value < toLocalDateString(new Date()) ? {dateBeforeToday: true} : null;
};

@Component({
  selector: 'app-add-certificate-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GtNgDatepickerComponent,
    DepartmentAutocompleteComponent,
    StandardCodeAutocompleteComponent,
    FileUploadComponent
  ],
  templateUrl: './user-certificate-modal.html',
  styleUrl: './user-certificate-modal.scss',
})
export class AddCertificateModalComponent implements OnInit {
  @Input() ownerName = '';
  @Input() taxId = '';
  @Input() mode: 'create' | 'edit' | 'view' = 'create';
  @Input() initialValue: CertificateEditValue | null = null;
  /**
   * A certificate that มกอช. does not hold (GMP, HACCP, …): the business keys it in itself and
   * saves straight away — there is nothing to verify it against, so DGA / ThaiD are not offered.
   */
  @Input() manualEntry = false;

  /** The scan picked in this modal (PDF / image), uploaded by the caller after the save. */
  protected readonly attachment = signal<File | null>(null);
  /** The user dropped the attachment that was on the certificate. */
  protected readonly attachmentRemoved = signal(false);
  protected readonly attachmentError = signal<string | null>(null);

  /** Existing attachment still on the certificate (edit / view), unless dropped in this modal. */
  protected get existingAttachmentUrl(): string | null {
    return this.attachmentRemoved() ? null : (this.initialValue?.attachmentUrl ?? null);
  }

  protected onAttachmentPicked(file: File | null): void {
    this.attachmentError.set(null);
    if (!file) {
      this.attachment.set(null);
      return;
    }
    const type = (file.type || '').toLowerCase();
    if (!['application/pdf', 'image/png', 'image/jpeg'].includes(type)) {
      this.attachment.set(null);
      this.attachmentError.set('รองรับเฉพาะไฟล์ PDF, PNG, JPG');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.attachment.set(null);
      this.attachmentError.set('ไฟล์ต้องมีขนาดไม่เกิน 5 MB');
      return;
    }
    this.attachment.set(file);
    this.attachmentRemoved.set(false);
  }

  protected dropExistingAttachment(): void {
    this.attachmentRemoved.set(true);
  }
  protected readonly today = toLocalDateString(new Date());

  private readonly fb = inject(FormBuilder);
  private readonly certificateApi = inject(UserCertificateApiService);
  protected readonly activeModal = inject(NgbActiveModal);

  readonly form = this.fb.group({
    certificateNo: ['', Validators.required],
    standardCode: [''],
    standardName: ['', Validators.required],
    productName: [''],
    issuingDepartment: [null as number | null, Validators.required],
    issuingDepartmentName: [''],
    description: [''],
    sizeFarm: [null as number | null],
    unitFarm: [''],
    startDate: ['', Validators.required],
    endDate: ['', [Validators.required, endDateNotBeforeTodayValidator]]
  });

  /**
   * Self-keyed layout: the standard and the issuer are typed in (no master holds them) and the
   * product / farm-size rows are dropped. Adding and editing always use it — the edit dialog is
   * the add dialog with the values filled in. A view keeps the master-backed layout only for a
   * certificate that still carries a department id and no typed issuer.
   */
  protected get manualLayout(): boolean {
    if (this.mode !== 'view') {
      return true;
    }
    return this.manualEntry
      || (this.initialValue != null
        && this.initialValue.issuingDepartment == null
        && !!`${this.initialValue.issuingDepartmentName ?? ''}`.trim());
  }

  ngOnInit(): void {
    if (this.manualLayout) {
      // The issuer is free text here; the department id is never asked for.
      this.form.controls.issuingDepartment.clearValidators();
      this.form.controls.issuingDepartmentName.addValidators(Validators.required);
    }
    if (this.mode !== 'view') {
      this.form.controls.certificateNo.addAsyncValidators(
        this.certificateApi.certificateNoValidator(this.initialValue?.id)
      );
    }

    if (this.initialValue) {
      this.form.patchValue({
        certificateNo: this.initialValue.certificateNo ?? '',
        standardCode: this.initialValue.standardCode ?? '',
        standardName: this.initialValue.standardName ?? '',
        productName: this.initialValue.productName ?? '',
        issuingDepartment: this.initialValue.issuingDepartment ?? null,
        issuingDepartmentName: this.initialValue.issuingDepartmentName ?? '',
        description: this.initialValue.description ?? '',
        sizeFarm: this.initialValue.sizeFarm ?? null,
        unitFarm: this.initialValue.unitFarm ?? '',
        startDate: this.toFormDate(this.initialValue.startDate),
        endDate: this.toFormDate(this.initialValue.endDate)
      }, {emitEvent: false});

      if (this.mode === 'view') {
        this.form.disable({emitEvent: false});
      }
    }

    if (this.mode !== 'view') {
      this.form.controls.certificateNo.updateValueAndValidity({emitEvent: false});
    }
  }

  protected isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.touched && control.invalid;
  }

  protected hasError(controlName: string, errorCode: string): boolean {
    return this.form.get(controlName)?.hasError(errorCode) ?? false;
  }

  protected isPending(controlName: string): boolean {
    return this.form.get(controlName)?.pending ?? false;
  }

  protected onStandardSelected(item: StandardCodeOptionDto | null): void {
    if (item) {
      const pre = (item.preCode || 'มกษ.').trim();
      const code = item.standardCode?.trim();
      const short = (item.shortStandard || item.shortStandardCode)?.trim();
      const parts: string[] = [];
      if (code) {
        parts.push(`${pre} ${code}`);
      }
      if (short) {
        parts.push(short);
      }
      const fullCode = parts.join(' ').trim();
      this.form.patchValue({standardCode: fullCode || item.title});
    } else {
      this.form.patchValue({standardCode: ''});
    }
  }

  /** The certificate-number check runs asynchronously; a click while it is running waits for it. */
  protected readonly checking = signal(false);

  protected async save(): Promise<void> {
    if (this.mode === 'view') {
      return;
    }
    await this.settleValidation();
    if (!this.hasValidCertificateForm()) {
      return;
    }
    const value = this.form.getRawValue();
    const standardName = this.toTextOrUndefined(value.standardName) ?? '';
    const standardCode = this.toTextOrUndefined(value.standardCode) ?? standardName;
    const manual = this.manualLayout;

    const result: CertificateModalResult = {
      certificateNo: this.toTextOrUndefined(value.certificateNo),
      standardCode: standardCode,
      standardName: standardName,
      productName: manual ? undefined : this.toTextOrUndefined(value.productName),
      issuingDepartment: !manual && value.issuingDepartment != null ? Number(value.issuingDepartment) : undefined,
      issuingDepartmentName: manual ? this.toTextOrUndefined(value.issuingDepartmentName) : undefined,
      description: this.toTextOrUndefined(value.description),
      sizeFarm: manual || value.sizeFarm === null || value.sizeFarm === undefined ? undefined : Number(value.sizeFarm),
      unitFarm: manual ? undefined : this.toTextOrUndefined(value.unitFarm),
      startDate: new Date(value.startDate),
      endDate: new Date(value.endDate)
    };
    const picked = this.attachment();
    if (picked) {
      result.attachment = picked;
    } else if (this.attachmentRemoved() && this.initialValue?.attachmentUrl) {
      result.removeAttachment = true;
    }
    this.activeModal.close(result);
  }

  /** Resolves once no control is still PENDING (the debounced duplicate-number lookup). */
  private async settleValidation(): Promise<void> {
    if (!this.form.pending || this.checking()) {
      return;
    }
    this.checking.set(true);
    try {
      await firstValueFrom(this.form.statusChanges.pipe(filter((status) => status !== 'PENDING'), take(1)));
    } finally {
      this.checking.set(false);
    }
  }

  private hasValidCertificateForm(): boolean {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.form.pending) {
      return false;
    }
    const value = this.form.getRawValue();
    if (!value.startDate || !value.endDate) {
      return false;
    }
    if (new Date(value.endDate) < new Date(value.startDate)) {
      return false;
    }
    return true;
  }

  protected cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  private toFormDate(value: Date | undefined): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }

  private toTextOrUndefined(value: string | null | undefined): string | undefined {
    const text = value?.trim();
    return text || undefined;
  }
}
